import { isValidYoutubeUrl } from '@tiptap/extension-youtube';
import { NodeSelection } from '@tiptap/pm/state';
import { type Editor } from '@tiptap/react';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { AnnouncementAudienceType } from '@/app/enums/AnnouncementAudienceType';
import { AnnouncementBodyFormat } from '@/app/enums/AnnouncementBodyFormat';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type AnnouncementPayload } from '@/app/models/AnnouncementPayload';
import { type AnnouncementValidityPayload } from '@/app/models/AnnouncementValidityPayload';
import { type ContactDto } from '@/app/models/Contact';
import { ANNOUNCEMENT_UPLOAD_SLOT_PREFIX } from '@/app/modules/announcements/core/_consts';
import { AnnouncementAlignment, AnnouncementMediaNode } from '@/app/modules/announcements/core/_models';
import { isAnnouncementMediaUrl } from '@/app/modules/announcements/core/_utils';
import { fromDateTimeLocalInput, toDateTimeLocalInput } from '@/app/pages/notifications/core/_utils';
import { type UserRoleDto } from '@/app/models/UserRoleDto';
import {
  type AnnouncementFormValues,
  type AnnouncementBodyContent,
  type AnnouncementMediaFile,
  type AnnouncementMediaFormat,
  getAnnouncementFormValues,
} from '@/app/pages/notifications/announcements/core/_models';

const MINUTE_IN_MS = 60_000;

const MAX_IMAGE_BYTES: number = 10_485_760;
const MAX_VIDEO_BYTES: number = 20_971_520;

// Os mesmos formatos e limites que o backend aceita no envio do comunicado.
const mediaFormats: Map<string, AnnouncementMediaFormat> = new Map([
  ['image/jpeg', { type: AnnouncementMediaNode.IMAGE, extensions: ['jpg', 'jpeg'], maxBytes: MAX_IMAGE_BYTES }],
  ['image/png', { type: AnnouncementMediaNode.IMAGE, extensions: ['png'], maxBytes: MAX_IMAGE_BYTES }],
  ['image/gif', { type: AnnouncementMediaNode.IMAGE, extensions: ['gif'], maxBytes: MAX_IMAGE_BYTES }],
  ['image/webp', { type: AnnouncementMediaNode.IMAGE, extensions: ['webp'], maxBytes: MAX_IMAGE_BYTES }],
  ['video/mp4', { type: AnnouncementMediaNode.VIDEO, extensions: ['mp4'], maxBytes: MAX_VIDEO_BYTES }],
  ['video/webm', { type: AnnouncementMediaNode.VIDEO, extensions: ['webm'], maxBytes: MAX_VIDEO_BYTES }],
]);

const MEDIA_SELECTOR = 'img, video';
const YOUTUBE_IFRAME_SELECTOR = 'div[data-youtube-video] iframe';

const getMediaSizeError = (type: AnnouncementMediaNode): string =>
  type === AnnouncementMediaNode.VIDEO ? 'O vídeo deve ter no máximo 20 MiB.' : 'A imagem deve ter no máximo 10 MiB.';

export const getAnnouncementMediaFile = (file: File): AnnouncementMediaFile => {
  const fileType = file.type.toLowerCase();
  const mime = fileType === 'image/jpg' ? 'image/jpeg' : fileType;
  const format = mediaFormats.get(mime);
  if (!format || file.size === 0) {
    throw new Error('Selecione uma imagem JPEG, PNG, GIF ou WebP, ou um vídeo MP4 ou WebM válido.');
  }
  if (file.size > format.maxBytes) {
    const sizeError = getMediaSizeError(format.type);
    throw new Error(sizeError);
  }
  const filename = file.name || `${format.type}-${crypto.randomUUID()}.${format.extensions[0]}`;
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';
  if (!format.extensions.includes(extension)) throw new Error('A extensão do arquivo deve corresponder ao formato.');
  return { file: new File([file], filename, { type: mime }), type: format.type };
};

const parseAnnouncementBody = (body: string): Document => new DOMParser().parseFromString(body, 'text/html');

/** As origens das imagens e vídeos do corpo, na ordem em que aparecem, inclusive os que ainda estão enviando. */
export const getAnnouncementMediaSources = (body: string): string[] => {
  const parsed = parseAnnouncementBody(body);
  const mediaElements = parsed.querySelectorAll(MEDIA_SELECTOR);
  return Array.from(mediaElements, (media) => media.getAttribute('src') ?? '');
};

/** Enquanto envia, a mídia fica no corpo com esta origem; o fim do envio troca pela URL pública. */
export const getAnnouncementMediaSlot = (id: string): string => `${ANNOUNCEMENT_UPLOAD_SLOT_PREFIX}${id}`;

const findSlottedMedia = (parsed: Document, id: string): Element[] => {
  const slot = getAnnouncementMediaSlot(id);
  const mediaElements = parsed.querySelectorAll(MEDIA_SELECTOR);
  const media = Array.from(mediaElements);
  return media.filter((element) => element.getAttribute('src') === slot);
};

/** O fim do envio troca a origem provisória pela URL pública. */
export const replaceAnnouncementMediaSlot = (body: string, id: string, publicUrl: string): string => {
  const parsed = parseAnnouncementBody(body);
  const slotted = findSlottedMedia(parsed, id);
  for (const media of slotted) media.setAttribute('src', publicUrl);
  return parsed.body.innerHTML;
};

/**
 * As mídias cujo envio terminou entre os dois corpos, da origem provisória para a URL pública. Fica vazio quando o
 * corpo novo muda mais do que essas origens, como na chegada de um rascunho.
 */
export const getFinishedMediaUploads = (currentBody: string, nextBody: string): Map<string, string> => {
  const current = parseAnnouncementBody(currentBody);
  const next = parseAnnouncementBody(nextBody);
  const currentMediaElements = current.querySelectorAll(MEDIA_SELECTOR);
  const nextMediaElements = next.querySelectorAll(MEDIA_SELECTOR);
  const finishedUploads = new Map<string, string>();
  if (currentMediaElements.length !== nextMediaElements.length) return finishedUploads;
  currentMediaElements.forEach((media, index) => {
    const currentSource = media.getAttribute('src') ?? '';
    const nextSource = nextMediaElements[index].getAttribute('src') ?? '';
    if (currentSource === nextSource || !currentSource.startsWith(ANNOUNCEMENT_UPLOAD_SLOT_PREFIX)) return;
    finishedUploads.set(currentSource, nextSource);
    media.setAttribute('src', nextSource);
  });
  const isOnlyUploadsFinished = current.body.innerHTML === next.body.innerHTML;
  return isOnlyUploadsFinished ? finishedUploads : new Map<string, string>();
};

// O envio falhou e a pessoa desistiu, ou foi cancelado: a mídia sai do corpo.
export const removeAnnouncementMediaSlot = (body: string, id: string): string => {
  const parsed = parseAnnouncementBody(body);
  const slotted = findSlottedMedia(parsed, id);
  for (const media of slotted) media.remove();
  return parsed.body.innerHTML;
};

// Só conta o iframe que o editor aceita como vídeo do YouTube; outro iframe some ao exibir.
const hasYoutubeEmbed = (parsed: Document): boolean => {
  const iframeElements = parsed.querySelectorAll(YOUTUBE_IFRAME_SELECTOR);
  const iframes = Array.from(iframeElements);
  return iframes.some((iframe) => {
    const source = iframe.getAttribute('src') ?? '';
    return !!isValidYoutubeUrl(source);
  });
};

export const getAnnouncementBodyContent = (body: string): AnnouncementBodyContent => {
  const parsed = parseAnnouncementBody(body);
  const hasText = parsed.body.textContent.trim() !== '';
  const hasYoutubeVideo = hasYoutubeEmbed(parsed);
  const sources = getAnnouncementMediaSources(body);
  const announcementMediaUrls = sources.filter(isAnnouncementMediaUrl);
  const mediaUrls = [...new Set(announcementMediaUrls)];
  const hasPendingMedia = sources.some((source) => source.startsWith(ANNOUNCEMENT_UPLOAD_SLOT_PREFIX));
  return { hasText, hasYoutubeVideo, mediaUrls, hasPendingMedia };
};

export const hasAnnouncementBodyContent = (body: string): boolean => {
  const content = getAnnouncementBodyContent(body);
  const hasVisibleContent = content.hasText || content.hasYoutubeVideo || content.mediaUrls.length > 0;
  return hasVisibleContent && !content.hasPendingMedia;
};

export const getAnnouncementAttachments = (body: string, attachments: AttachmentDto[]): AttachmentDto[] => {
  const content = getAnnouncementBodyContent(body);
  const mediaUrls = new Set(content.mediaUrls);
  return attachments.filter((attachment) => !!attachment.public_url && mediaUrls.has(attachment.public_url));
};

const MAX_CUSTOM_COLORS: number = 7;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/;

/** As cores escolhidas fora da paleta, da mais antiga à mais nova; guardadas só neste navegador. */
export const loadAnnouncementCustomColors = (storageKey: string): string[] => {
  try {
    const storedColors = globalThis.localStorage.getItem(storageKey) ?? '[]';
    const stored: unknown = JSON.parse(storedColors);
    if (!Array.isArray(stored)) return [];
    const colors = stored.filter(
      (color): color is string => typeof color === 'string' && HEX_COLOR_PATTERN.test(color),
    );
    return colors.slice(-MAX_CUSTOM_COLORS);
  } catch {
    return [];
  }
};

// Sem acesso ao armazenamento (aba anônima, cota cheia), a cor vale só até fechar a página.
export const saveAnnouncementCustomColors = (storageKey: string, colors: string[]): void => {
  try {
    const serializedColors = JSON.stringify(colors);
    globalThis.localStorage.setItem(storageKey, serializedColors);
  } catch {
    // A lista continua no estado do menu.
  }
};

/** A cor nova vai para o fim; repetida, só muda de lugar; passando de sete, sai a mais antiga. */
export const addAnnouncementCustomColor = (colors: string[], color: string): string[] => {
  const normalizedColor = color.toLowerCase();
  const otherColors = colors.filter((current) => current !== normalizedColor);
  return [...otherColors, normalizedColor].slice(-MAX_CUSTOM_COLORS);
};

// O backend grava LocalDateTime sem fuso: "agora" vai no horário local, não em UTC.
const nowAsLocalDateTime = (): string => {
  const now = new Date();
  const localNow = new Date(now.getTime() - now.getTimezoneOffset() * MINUTE_IN_MS);
  return localNow.toISOString().slice(0, 19);
};

const toOptionalLocalDateTime = (value: string): string | null => (value ? fromDateTimeLocalInput(value) : null);

export const mapFormValuesToPayload = (values: AnnouncementFormValues): AnnouncementPayload => {
  const startDate = values.startsNow ? nowAsLocalDateTime() : fromDateTimeLocalInput(values.startDate);
  const forRoles = values.audienceType === AnnouncementAudienceType.ROLES;
  const forUsers = values.audienceType === AnnouncementAudienceType.USERS;
  const attachments = getAnnouncementAttachments(values.body, values.attachments);

  return {
    title: values.title.trim(),
    body: values.body,
    body_format: AnnouncementBodyFormat.HTML,
    attachments,
    type: values.type,
    audience_type: values.audienceType,
    user_roles: forRoles ? values.userRoleIds.map((id) => ({ id })) : [],
    users: forUsers ? values.userIds.map((id) => ({ id })) : [],
    start_date: startDate,
    end_date: toOptionalLocalDateTime(values.endDate),
    email_enabled: values.emailEnabled,
  };
};

export const mapAnnouncementToFormValues = (announcement: AnnouncementDto): AnnouncementFormValues => {
  const endDate = announcement.end_date ?? null;
  const values: Partial<AnnouncementFormValues> = {
    title: announcement.title,
    body: announcement.body,
    attachments: announcement.attachments,
    type: announcement.type.value,
    audienceType: announcement.audience_type.value,
    userRoleIds: announcement.user_roles?.map((role) => role.id) ?? [],
    userIds: announcement.users?.map((user) => user.id) ?? [],
    startsNow: false,
    startDate: toDateTimeLocalInput(announcement.start_date),
    endDate: toDateTimeLocalInput(endDate),
    emailEnabled: announcement.email_enabled,
  };
  return getAnnouncementFormValues(values);
};

/** Duplicar copia texto, público e o envio por e-mail; a vigência recomeça. */
export const mapAnnouncementToDuplicateValues = (announcement: AnnouncementDto): AnnouncementFormValues => {
  const sourceValues = mapAnnouncementToFormValues(announcement);
  const values: Partial<AnnouncementFormValues> = {
    ...sourceValues,
    startsNow: true,
    startDate: '',
    endDate: '',
  };
  return getAnnouncementFormValues(values);
};

// O input corta os segundos; reenviar o início cortado conta como mudança, que o backend recusa se já começou.
export const mapValidityFormValuesToPayload = (
  originalStartDate: string,
  startDate: string,
  endDate: string,
): AnnouncementValidityPayload => {
  const startUnchanged = startDate === toDateTimeLocalInput(originalStartDate);
  return {
    start_date: startUnchanged ? originalStartDate : fromDateTimeLocalInput(startDate),
    end_date: toOptionalLocalDateTime(endDate),
  };
};

/** Escolher uma pessoa leva todos os acessos ativos dela. */
export const userIdsOf = (contacts: ContactDto[]): string[] =>
  contacts
    .flatMap((contact) => contact.users ?? [])
    .filter((user) => user.active !== false)
    .flatMap((user) => (user.id ? [user.id] : []));

export const selectedContacts = (contacts: ContactDto[], userIds: string[]): ContactDto[] => {
  const selectedUserIds = new Set(userIds);
  return contacts.filter((contact) => userIdsOf([contact]).some((userId) => selectedUserIds.has(userId)));
};

/** Os usuários escolhidos que não vieram na lista carregada, como os além do limite de contatos. */
export const unlistedUserIds = (contacts: ContactDto[], userIds: string[]): string[] => {
  const contactUserIds = userIdsOf(contacts);
  const listedUserIds = new Set(contactUserIds);
  return userIds.filter((userId) => !listedUserIds.has(userId));
};

const MEDIA_NODE_ENTRIES = Object.values(AnnouncementMediaNode).map((node): [string, AnnouncementMediaNode] => [
  node,
  node,
]);
const MEDIA_NODES_BY_NAME = new Map<string, AnnouncementMediaNode>(MEDIA_NODE_ENTRIES);

/** O tipo da imagem, vídeo ou YouTube selecionado; com texto selecionado, nenhum. */
export const getSelectedMediaType = (editor: Editor): AnnouncementMediaNode | undefined => {
  const { selection } = editor.state;
  if (!(selection instanceof NodeSelection)) return undefined;
  return MEDIA_NODES_BY_NAME.get(selection.node.type.name);
};

const TEXT_ALIGNMENTS: AnnouncementAlignment[] = [
  AnnouncementAlignment.CENTER,
  AnnouncementAlignment.RIGHT,
  AnnouncementAlignment.JUSTIFY,
];

export const isAlignedTo = (editor: Editor, alignment: AnnouncementAlignment): boolean => {
  const mediaType = getSelectedMediaType(editor);
  if (mediaType) {
    const mediaAlignment = editor.getAttributes(mediaType).align as AnnouncementAlignment | null;
    return (mediaAlignment ?? AnnouncementAlignment.LEFT) === alignment;
  }
  if (alignment === AnnouncementAlignment.LEFT) {
    return !TEXT_ALIGNMENTS.some((textAlign) => editor.isActive({ textAlign }));
  }
  return editor.isActive({ textAlign: alignment });
};

// À esquerda é o padrão: o botão tira o alinhamento em vez de gravar "left" no HTML.
export const alignSelection = (editor: Editor, alignment: AnnouncementAlignment) => {
  const mediaType = getSelectedMediaType(editor);
  const storedAlignment = alignment === AnnouncementAlignment.LEFT ? null : alignment;
  if (mediaType) {
    editor.chain().focus().updateAttributes(mediaType, { align: storedAlignment }).run();
    return;
  }
  if (storedAlignment) editor.chain().focus().setTextAlign(storedAlignment).run();
  else editor.chain().focus().unsetTextAlign().run();
};

export const roleIdsOf = (roles: UserRoleDto[]): string[] => roles.flatMap((role) => (role.id ? [role.id] : []));

/** As funções escolhidas que não vieram na lista carregada, como as além do limite de funções. */
export const unlistedRoleIds = (roles: UserRoleDto[], userRoleIds: string[]): string[] => {
  const loadedRoleIds = roleIdsOf(roles);
  const listedRoleIds = new Set(loadedRoleIds);
  return userRoleIds.filter((roleId) => !listedRoleIds.has(roleId));
};

// O tipo do contato separa homônimos: o funcionário e o contador com o mesmo nome.
export const contactLabel = (contact: ContactDto): string => {
  const typeDescription = contact.type?.description;
  return typeDescription ? `${contact.name} · ${typeDescription}` : contact.name;
};

export const toggleLink = (editor: Editor) => {
  if (editor.isActive('link')) {
    editor.chain().focus().unsetLink().run();
    return;
  }
  const href = globalThis.prompt('Endereço do link');
  if (!href) return;
  editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
};

export const insertYoutubeVideo = (editor: Editor) => {
  const src = globalThis.prompt('Link do vídeo do YouTube');
  if (!src) return;
  const inserted = editor.chain().focus().setYoutubeVideo({ src }).run();
  if (!inserted) SystemNotification.error('Informe um link de vídeo do YouTube.');
};
